import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import { moduleColor } from '../ui/modulePalette';
import {
  IconChart,
  IconAlertTriangle,
  IconBan,
  IconCheck,
  IconClock,
  IconCode,
  IconCopy,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFolder,
  IconGlobe,
  IconImage,
  IconKey,
  IconLayers,
  IconPin,
  IconPlus,
  IconSearch,
  IconSparkles,
  IconTag,
  IconTrash,
  IconUsers,
  IconUtensils,
} from '../ui/icons';

function deepClone(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value && typeof value === 'object' ? { ...(value || {}) } : value;
  }
}

function newBlockId() {
  return `b_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
}

const MODULE_ACTION_ICON = {
  projects: IconFileText,
  menus: IconUtensils,
  resources: IconFolder,
  clients: IconUsers,
  updates: IconSparkles,
  appointments: IconPin,
  gallery: IconImage,
  traffic: IconChart,
};

const BLOCK_LIBRARY_BASE = [
  { type: 'set_var', category: 'Variables', label: 'Définir une variable', hint: 'Stocke une valeur locale', icon: IconTag, color: '#38bdf8' },
  { type: 'template', category: 'Variables', label: 'Template texte', hint: 'Compose un texte avec [[variables]]', icon: IconEdit, color: '#a78bfa' },
  { type: 'date_now', category: 'Variables', label: 'Date / heure', hint: 'Insère la date courante', icon: IconClock, color: '#22c55e' },
  { type: 'math', category: 'Variables', label: 'Calcul', hint: 'Addition, soustraction, etc.', icon: IconLayers, color: '#f59e0b' },
  { type: 'random_uuid', category: 'Variables', label: 'UUID aléatoire', hint: 'Génère un identifiant unique', icon: IconKey, color: '#06b6d4' },

  { type: 'ask_text', category: 'Saisie Utilisateur', label: 'Demander un texte', hint: 'Champ libre requis/optionnel', icon: IconEdit, color: '#f97316' },
  { type: 'ask_number', category: 'Saisie Utilisateur', label: 'Demander un nombre', hint: 'Saisie numérique validée', icon: IconLayers, color: '#f97316' },
  { type: 'ask_select', category: 'Saisie Utilisateur', label: 'Afficher un menu', hint: 'Choix simple ou multisélection', icon: IconSearch, color: '#f97316' },
  { type: 'ask_confirm', category: 'Saisie Utilisateur', label: 'Demander confirmation', hint: 'Oui / Non', icon: IconCheck, color: '#f97316' },

  { type: 'http_request', category: 'API & Réseau', label: 'Requête HTTP', hint: 'Appel API sécurisé (allowlist)', icon: IconGlobe, color: '#ef4444' },
  { type: 'json_extract', category: 'API & Réseau', label: 'Extraction JSON', hint: 'Lit une valeur dans un objet', icon: IconSearch, color: '#ef4444' },

  { type: 'list_projects', category: 'Projets', moduleKey: 'projects', label: 'Lister les projets', hint: 'Lecture des projets du site' },
  { type: 'project_get_description', category: 'Projets', moduleKey: 'projects', label: 'Lire description projet', hint: 'Récupère la description d’un projet', comingSoon: true },
  { type: 'project_create', category: 'Projets', moduleKey: 'projects', label: 'Créer un projet', hint: 'Ajoute un projet (à venir)', comingSoon: true },

  { type: 'menu_update_card', category: 'Menus', moduleKey: 'menus', label: 'Changer une carte', hint: 'Met à jour une section de menu', comingSoon: true },
  { type: 'menu_create_formula', category: 'Menus', moduleKey: 'menus', label: 'Créer une formule', hint: 'Ajoute une formule restaurateur', comingSoon: true },

  { type: 'list_resources', category: 'Ressources', moduleKey: 'resources', label: 'Lister les ressources', hint: 'Lecture médias/fichiers' },
  { type: 'resources_move_file', category: 'Ressources', moduleKey: 'resources', label: 'Déplacer un fichier', hint: 'Vers un dossier cible', comingSoon: true },
  { type: 'list_contacts', category: 'Contacts', moduleKey: 'clients', label: 'Lister les contacts', hint: 'Lecture des contacts liés' },

  { type: 'create_file', category: 'Sorties', label: 'Créer un fichier', hint: 'Génère un fichier en mémoire', icon: IconFileText, color: '#10b981' },
  { type: 'show', category: 'Sorties', label: 'Afficher dans un embed', hint: 'Affiche un texte/valeur dans un embed', icon: IconFileText, color: '#22c55e' },
  { type: 'notify', category: 'Sorties', label: 'Notification', hint: 'Écrit un log métier', icon: IconCheck, color: '#84cc16' },
  { type: 'create_update', category: 'Sorties', moduleKey: 'updates', label: 'Créer une mise à jour', hint: 'Écriture dans Mises à jour' },
];

const BLOCK_LIBRARY = BLOCK_LIBRARY_BASE.map((item) => {
  const key = String(item?.moduleKey || '').trim();
  if (!key) return item;
  const moduleIcon = MODULE_ACTION_ICON[key] || IconCode;
  return {
    ...item,
    icon: item.icon || moduleIcon,
    color: item.color || moduleColor(key),
  };
});

const BLOCK_META_BY_TYPE = BLOCK_LIBRARY.reduce((acc, it) => {
  acc[it.type] = it;
  return acc;
}, {});

function defaultPermissions() {
  return {
    allow_http: false,
    allow_generate_files: true,
    allow_projects_read: true,
    allow_resources_read: false,
    allow_contacts_read: false,
    allow_updates_write: false,
    allow_notify: true,
    max_http_calls: 5,
    max_blocks: 100,
  };
}

function normalizePermissions(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const base = defaultPermissions();
  const bool = (value, fallback) => {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1' || value === 'true') return true;
    if (value === 0 || value === '0' || value === 'false') return false;
    return fallback;
  };
  return {
    allow_http: bool(src.allow_http, base.allow_http),
    allow_generate_files: bool(src.allow_generate_files, base.allow_generate_files),
    allow_projects_read: bool(src.allow_projects_read, base.allow_projects_read),
    allow_resources_read: bool(src.allow_resources_read, base.allow_resources_read),
    allow_contacts_read: bool(src.allow_contacts_read, base.allow_contacts_read),
    allow_updates_write: bool(src.allow_updates_write, base.allow_updates_write),
    allow_notify: bool(src.allow_notify, base.allow_notify),
    max_http_calls: Math.max(0, Math.min(50, Number(src.max_http_calls ?? base.max_http_calls) || base.max_http_calls)),
    max_blocks: Math.max(1, Math.min(500, Number(src.max_blocks ?? base.max_blocks) || base.max_blocks)),
  };
}

function defaultBlock(type) {
  const id = newBlockId();
  if (type === 'set_var') return { id, type, key: 'value', value: '' };
  if (type === 'template') return { id, type, key: 'text', template: 'Bonjour [[name]]' };
  if (type === 'date_now') return { id, type, key: 'now', format: 'datetime' };
  if (type === 'math') return { id, type, key: 'result', op: 'add', left: 0, right: 0 };
  if (type === 'random_uuid') return { id, type, key: 'uuid' };
  if (type === 'ask_text') {
    return {
      id,
      type,
      key: 'answer_text',
      label: 'Question',
      placeholder: '',
      required: true,
      default: '',
      min_length: '',
      max_length: '',
    };
  }
  if (type === 'ask_number') {
    return {
      id,
      type,
      key: 'answer_number',
      label: 'Nombre',
      required: true,
      default: 0,
      min: '',
      max: '',
      allow_decimals: true,
      max_decimals: 2,
    };
  }
  if (type === 'ask_select') {
    return {
      id,
      type,
      key: 'answer_choice',
      label: 'Choisis une valeur',
      options: ['Option A', 'Option B'],
      multiple: false,
      required: true,
    };
  }
  if (type === 'ask_confirm') return { id, type, key: 'answer_confirm', label: 'Confirmer ?', default: false };
  if (type === 'http_request') {
    return {
      id,
      type,
      method: 'GET',
      url: 'https://api.github.com/zen',
      body: '',
      headers: {},
      save_as: 'http_response',
    };
  }
  if (type === 'json_extract') return { id, type, from: 'http_response.body_json', path: '', save_as: 'extract' };
  if (type === 'create_file') {
    return {
      id,
      type,
      filename: 'output.txt',
      mime: 'text/plain',
      template: '[[extract]]',
      save_as: 'generated_file',
    };
  }
  if (type === 'show') {
    return {
      id,
      type,
      title: 'Affichage',
      template: 'Valeur: [[value]]',
      from: '',
    };
  }
  if (type === 'notify') return { id, type, message: 'Workflow exécuté' };
  if (type === 'list_projects') return { id, type, limit: 120, save_as: 'projects' };
  if (type === 'list_resources') return { id, type, limit: 120, include_hidden: false, save_as: 'resources' };
  if (type === 'list_contacts') return { id, type, limit: 80, save_as: 'contacts' };
  if (type === 'create_update') {
    return {
      id,
      type,
      title: 'Mise à jour automatique',
      level: 'minor',
      changes: 'Changement généré via Scripts',
      save_as: 'created_update_id',
    };
  }
  if (type === 'project_get_description') return { id, type, project_id: '', save_as: 'project_description' };
  if (type === 'project_create') return { id, type, name: 'Nouveau projet', save_as: 'created_project_id' };
  if (type === 'menu_update_card') return { id, type, menu_category_id: '', title: 'Nouveau titre', save_as: 'menu_result' };
  if (type === 'menu_create_formula') return { id, type, title: 'Formule', price: '', save_as: 'formula_result' };
  if (type === 'resources_move_file') return { id, type, media_id: '', folder_id: '', save_as: 'move_result' };
  return { id, type, key: 'value', value: '' };
}

function normalizeBlock(raw, index = 0) {
  const type = String(raw?.type || '').trim();
  const base = defaultBlock(type || 'set_var');
  const id = String(raw?.id || '').trim() || `b_${index}`;
  const next = { ...base, ...(raw && typeof raw === 'object' ? raw : {}), id, type: base.type };
  if (type && BLOCK_META_BY_TYPE[type]) next.type = type;
  if (next.type === 'http_request' && typeof next.headers === 'string') {
    try {
      const parsed = JSON.parse(next.headers);
      if (parsed && typeof parsed === 'object') next.headers = parsed;
    } catch {
      // keep as string; backend will ignore if not valid JSON object
    }
  }
  return next;
}

function normalizeWorkflow(raw, permissions) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const blocksRaw = Array.isArray(src.blocks) ? src.blocks : [];
  const maxBlocks = Math.max(1, Math.min(500, Number(permissions?.max_blocks || 100) || 100));
  const networkRaw = src.network && typeof src.network === 'object' ? src.network : {};
  const hostsRaw = Array.isArray(networkRaw.allowed_hosts) ? networkRaw.allowed_hosts : [];
  const allowedHosts = [];
  for (const hostRaw of hostsRaw) {
    let host = String(hostRaw || '').trim().toLowerCase();
    if (!host) continue;
    host = host.replace(/^https?:\/\//i, '');
    host = host.replace(/\/.*$/, '');
    if (!/^[a-z0-9.-]+$/.test(host)) continue;
    if (!allowedHosts.includes(host)) allowedHosts.push(host);
    if (allowedHosts.length >= 50) break;
  }
  return {
    version: 1,
    title: String(src.title || '').trim() || 'Nouveau script',
    description: String(src.description || '').trim(),
    network: { allowed_hosts: allowedHosts },
    blocks: blocksRaw.slice(0, maxBlocks).map((b, i) => normalizeBlock(b, i)),
  };
}

function toDraft(script) {
  const s = script || {};
  const permissions = normalizePermissions(s.permissions);
  return {
    id: Number(s.id || 0),
    name: String(s.name || ''),
    source_js: String(s.source_js || ''),
    is_enabled: Boolean(s.is_enabled),
    sort_order: Number(s.sort_order || 0),
    workflow: normalizeWorkflow(s.workflow, permissions),
    permissions,
  };
}

function draftFingerprint(draft) {
  if (!draft) return '';
  return JSON.stringify({
    id: Number(draft.id || 0),
    name: String(draft.name || ''),
    source_js: String(draft.source_js || ''),
    is_enabled: Boolean(draft.is_enabled),
    sort_order: Number(draft.sort_order || 0),
    workflow: draft.workflow || null,
    permissions: draft.permissions || null,
  });
}

function formatDateTime(input) {
  if (!input) return '';
  const d = new Date(String(input).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(input);
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return String(input);
  }
}

function prettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value || '');
  }
}

function coerceNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function parseRunInputObject(rawText) {
  let input = {};
  const raw = String(rawText || '').trim();
  if (!raw) return { ok: true, input };
  try {
    input = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'Entrée JSON invalide.' };
  }
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, error: 'Entrée JSON invalide: un objet est attendu.' };
  }
  return { ok: true, input };
}

function asPromptKey(raw, fallback) {
  const key = String(raw || '').trim();
  return key || fallback;
}

function countDecimals(value) {
  const raw = String(value ?? '').trim().replace(',', '.');
  const m = raw.match(/^-?\d+(?:\.(\d+))?$/);
  if (!m) return 0;
  return m[1] ? m[1].length : 0;
}

function buildPromptSteps(blocks, baseInput) {
  const steps = [];
  const list = Array.isArray(blocks) ? blocks : [];
  for (const block of list) {
    const type = String(block?.type || '').trim();
    if (type !== 'ask_text' && type !== 'ask_number' && type !== 'ask_select' && type !== 'ask_confirm') continue;
    const key =
      type === 'ask_text'
        ? asPromptKey(block?.key, 'answer_text')
        : type === 'ask_number'
        ? asPromptKey(block?.key, 'answer_number')
        : type === 'ask_select'
        ? asPromptKey(block?.key, 'answer_choice')
        : asPromptKey(block?.key, 'answer_confirm');
    const valueFromInput = Object.prototype.hasOwnProperty.call(baseInput || {}, key) ? baseInput[key] : undefined;
    const common = {
      id: String(block?.id || newBlockId()),
      type,
      key,
      label: String(block?.label || '').trim() || 'Saisie',
      required: Boolean(block?.required),
    };
    if (type === 'ask_text') {
      const fallback = String(block?.default || '');
      steps.push({
        ...common,
        placeholder: String(block?.placeholder || ''),
        min_length: block?.min_length == null || block?.min_length === '' ? '' : Number(block.min_length),
        max_length: block?.max_length == null || block?.max_length === '' ? '' : Number(block.max_length),
        value: valueFromInput == null ? fallback : String(valueFromInput),
      });
      continue;
    }
    if (type === 'ask_number') {
      const fallback = block?.default ?? '';
      steps.push({
        ...common,
        min: block?.min == null || block?.min === '' ? '' : Number(block.min),
        max: block?.max == null || block?.max === '' ? '' : Number(block.max),
        allow_decimals: block?.allow_decimals == null ? true : Boolean(block.allow_decimals),
        max_decimals:
          block?.max_decimals == null || block?.max_decimals === ''
            ? ''
            : Math.max(0, Number(block.max_decimals)),
        value: valueFromInput == null ? fallback : valueFromInput,
      });
      continue;
    }
    if (type === 'ask_select') {
      const options = (Array.isArray(block?.options) ? block.options : [])
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      const multiple = Boolean(block?.multiple);
      const fallback = multiple ? [] : '';
      const value = multiple
        ? valueFromInput == null
          ? fallback
          : Array.isArray(valueFromInput)
          ? valueFromInput.map((x) => String(x || ''))
          : [String(valueFromInput || '')].filter(Boolean)
        : valueFromInput == null
        ? fallback
        : String(valueFromInput || '');
      steps.push({
        ...common,
        options,
        multiple,
        value,
      });
      continue;
    }
    const fallback = Boolean(block?.default);
    steps.push({
      ...common,
      value: valueFromInput == null ? fallback : Boolean(valueFromInput),
    });
  }
  return steps;
}

function promptValueIsValid(step, value) {
  if (!step || typeof step !== 'object') return true;
  const type = String(step.type || '');
  if (type === 'ask_text') {
    const raw = String(value || '');
    const t = raw.trim();
    const len = raw.length;
    const minLen = step.min_length === '' || !Number.isFinite(Number(step.min_length)) ? null : Math.max(0, Number(step.min_length));
    const maxLen = step.max_length === '' || !Number.isFinite(Number(step.max_length)) ? null : Math.max(0, Number(step.max_length));
    if (step.required && t === '') return false;
    if (minLen != null && len < minLen) return false;
    if (maxLen != null && len > maxLen) return false;
    return true;
  }
  if (type === 'ask_number') {
    if (!step.required && (value === '' || value == null)) return true;
    const raw = String(value ?? '').trim().replace(',', '.');
    if (raw === '') return !step.required;
    if (!Number.isFinite(Number(raw))) return false;
    const n = Number(raw);
    const min = step.min === '' || !Number.isFinite(Number(step.min)) ? null : Number(step.min);
    const max = step.max === '' || !Number.isFinite(Number(step.max)) ? null : Number(step.max);
    if (min != null && n < min) return false;
    if (max != null && n > max) return false;
    if (!Boolean(step.allow_decimals) && !Number.isInteger(n)) return false;
    if (Boolean(step.allow_decimals)) {
      const maxDecimals =
        step.max_decimals === '' || !Number.isFinite(Number(step.max_decimals))
          ? null
          : Math.max(0, Math.floor(Number(step.max_decimals)));
      if (maxDecimals != null && countDecimals(raw) > maxDecimals) return false;
    }
    return true;
  }
  if (type === 'ask_select') {
    const options = Array.isArray(step.options) ? step.options : [];
    if (step.multiple) {
      const arr = Array.isArray(value) ? value.map((x) => String(x || '')) : [];
      if (step.required && arr.length === 0) return false;
      if (!options.length) return true;
      return arr.every((x) => options.includes(x));
    }
    const v = String(value || '').trim();
    if (step.required && !v) return false;
    if (!v || !options.length) return true;
    return options.includes(v);
  }
  if (type === 'ask_confirm') return true;
  return true;
}

function promptValidationMessage(step, value) {
  if (!step || typeof step !== 'object') return 'Valeur invalide ou manquante pour cette étape.';
  const type = String(step.type || '');
  if (type === 'ask_text') {
    const raw = String(value || '');
    const t = raw.trim();
    const len = raw.length;
    const minLen = step.min_length === '' || !Number.isFinite(Number(step.min_length)) ? null : Math.max(0, Number(step.min_length));
    const maxLen = step.max_length === '' || !Number.isFinite(Number(step.max_length)) ? null : Math.max(0, Number(step.max_length));
    if (step.required && t === '') return 'Une valeur texte est requise.';
    if (minLen != null && len < minLen) return `Minimum ${minLen} caractère(s).`;
    if (maxLen != null && len > maxLen) return `Maximum ${maxLen} caractère(s).`;
    return 'Valeur invalide.';
  }
  if (type === 'ask_number') {
    const raw = String(value ?? '').trim().replace(',', '.');
    if (step.required && raw === '') return 'Une valeur numérique est requise.';
    if (raw === '') return 'Valeur invalide.';
    if (!Number.isFinite(Number(raw))) return 'Nombre invalide.';
    const n = Number(raw);
    const min = step.min === '' || !Number.isFinite(Number(step.min)) ? null : Number(step.min);
    const max = step.max === '' || !Number.isFinite(Number(step.max)) ? null : Number(step.max);
    if (min != null && n < min) return `Le nombre doit être >= ${min}.`;
    if (max != null && n > max) return `Le nombre doit être <= ${max}.`;
    if (!Boolean(step.allow_decimals) && !Number.isInteger(n)) return 'Seuls les nombres entiers sont autorisés.';
    if (Boolean(step.allow_decimals)) {
      const maxDecimals =
        step.max_decimals === '' || !Number.isFinite(Number(step.max_decimals))
          ? null
          : Math.max(0, Math.floor(Number(step.max_decimals)));
      if (maxDecimals != null && countDecimals(raw) > maxDecimals) {
        return `Maximum ${maxDecimals} décimale(s).`;
      }
    }
    return 'Nombre invalide.';
  }
  if (type === 'ask_select') return 'Choisis une valeur autorisée.';
  return 'Valeur invalide ou manquante pour cette étape.';
}

function downloadTextFile(name, content, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([String(content || '')], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = String(name || 'download.txt');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

function downloadGeneratedFile(file) {
  const name = String(file?.filename || 'output.bin');
  const mime = String(file?.mime || 'application/octet-stream');
  const base64 = String(file?.content_base64 || '');
  const url = `data:${mime};base64,${base64}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function ToggleLine({ label, description, value, onChange, disabled }) {
  return (
    <div className="ScriptsToggleLine">
      <div>
        <div className="LabelStrong">{label}</div>
        {description ? <div className="Muted" style={{ marginTop: 3 }}>{description}</div> : null}
      </div>
      <button
        type="button"
        className={`TogglePill ${value ? 'is-on' : ''}`}
        onClick={() => onChange?.(!value)}
        disabled={disabled}
      >
        {value ? 'Oui' : 'Non'}
      </button>
    </div>
  );
}

function textareaCaretPosition(textarea) {
  const el = textarea;
  if (!el) return { left: 8, top: 8 };
  const div = document.createElement('div');
  const style = window.getComputedStyle(el);
  const props = [
    'boxSizing',
    'width',
    'height',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
    'MozTabSize',
    'whiteSpace',
  ];
  props.forEach((prop) => {
    div.style[prop] = style[prop];
  });
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';
  div.style.overflow = 'hidden';
  div.textContent = el.value.substring(0, el.selectionStart || 0);
  const span = document.createElement('span');
  span.textContent = el.value.substring(el.selectionStart || 0) || '.';
  div.appendChild(span);
  document.body.appendChild(div);
  const left = span.offsetLeft - el.scrollLeft;
  const top = span.offsetTop - el.scrollTop;
  document.body.removeChild(div);
  return { left, top };
}

function VariableTextarea({
  value,
  onChange,
  rows = 4,
  placeholder = '',
  className = 'Textarea',
  variables = [],
}) {
  const ref = useRef(null);
  const [popup, setPopup] = useState({ open: false, items: [], active: 0, left: 8, top: 8, token: '' });

  function updatePopupForCaret(nextValue = null) {
    const el = ref.current;
    if (!el) return;
    const text = String(nextValue != null ? nextValue : el.value || '');
    const caret = Number(el.selectionStart || 0);
    const chunk = text.slice(0, caret);
    const m = chunk.match(/\[\[([a-zA-Z0-9_.-]*)$/);
    if (!m) {
      setPopup((p) => ({ ...p, open: false }));
      return;
    }
    const token = String(m[1] || '').toLowerCase();
    const items = (variables || [])
      .filter((v) => String(v || '').toLowerCase().includes(token))
      .slice(0, 12);
    if (!items.length) {
      setPopup((p) => ({ ...p, open: false }));
      return;
    }
    const caretPos = textareaCaretPosition(el);
    setPopup({
      open: true,
      items,
      active: 0,
      left: Math.max(8, caretPos.left + 4),
      top: Math.max(8, caretPos.top - 6),
      token,
    });
  }

  function applyVariable(name) {
    const el = ref.current;
    if (!el) return;
    const text = String(el.value || '');
    const caret = Number(el.selectionStart || 0);
    const before = text.slice(0, caret);
    const m = before.match(/\[\[([a-zA-Z0-9_.-]*)$/);
    if (!m) return;
    const start = caret - m[0].length;
    const next = `${text.slice(0, start)}[[${name}]]${text.slice(caret)}`;
    onChange?.(next);
    requestAnimationFrame(() => {
      try {
        const pos = start + String(`[[${name}]]`).length;
        ref.current?.setSelectionRange(pos, pos);
      } catch {
        // ignore
      }
    });
    setPopup((p) => ({ ...p, open: false }));
  }

  return (
    <div className="ScriptsVarTextareaWrap">
      <textarea
        ref={ref}
        className={className}
        rows={rows}
        value={String(value || '')}
        placeholder={placeholder}
        onChange={(e) => {
          const next = e.target.value;
          onChange?.(next);
          updatePopupForCaret(next);
        }}
        onClick={() => updatePopupForCaret()}
        onKeyUp={() => updatePopupForCaret()}
        onBlur={() => {
          setTimeout(() => {
            setPopup((p) => ({ ...p, open: false }));
          }, 120);
        }}
        onKeyDown={(e) => {
          if (!popup.open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setPopup((p) => ({ ...p, active: Math.min(p.items.length - 1, p.active + 1) }));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setPopup((p) => ({ ...p, active: Math.max(0, p.active - 1) }));
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const item = popup.items[popup.active];
            if (item) applyVariable(item);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setPopup((p) => ({ ...p, open: false }));
          }
        }}
      />
      {popup.open ? (
        <div
          className="ScriptsVarPopup"
          style={{ left: popup.left, top: popup.top }}
        >
          {popup.items.map((item, idx) => (
            <button
              key={item}
              type="button"
              className={`ScriptsVarOption ${idx === popup.active ? 'is-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                applyVariable(item);
              }}
            >
              {item}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BlockEditor({
  block,
  index,
  total,
  onPatch,
  onDelete,
  onMove,
  onDuplicate,
  onDragStart,
  onDragEnd,
  isDragging,
  variableSuggestions,
}) {
  const meta = BLOCK_META_BY_TYPE[String(block?.type || '')] || BLOCK_LIBRARY[0];
  const Icon = meta.icon || IconCode;
  const [conditionsOpen, setConditionsOpen] = useState(false);

  function patchHeadersFromText(nextText) {
    const txt = String(nextText || '').trim();
    if (!txt) {
      onPatch({ headers: {} });
      return;
    }
    try {
      const parsed = JSON.parse(txt);
      if (parsed && typeof parsed === 'object') {
        onPatch({ headers: parsed });
        return;
      }
    } catch {
      // ignore parse issue; keep raw string so user can continue editing
    }
    onPatch({ headers: txt });
  }

  const textCondLabel = (() => {
    const min = block?.min_length;
    const max = block?.max_length;
    const hasMin = min != null && String(min) !== '';
    const hasMax = max != null && String(max) !== '';
    if (!hasMin && !hasMax) return 'Aucune';
    if (hasMin && hasMax) return `${min}-${max} caractères`;
    if (hasMin) return `min ${min}`;
    return `max ${max}`;
  })();

  const numberCondLabel = (() => {
    const min = block?.min;
    const max = block?.max;
    const hasMin = min != null && String(min) !== '';
    const hasMax = max != null && String(max) !== '';
    const allowDecimals = block?.allow_decimals == null ? true : Boolean(block.allow_decimals);
    const maxDecimals = block?.max_decimals;
    const decimalPart = allowDecimals
      ? maxDecimals == null || String(maxDecimals) === ''
        ? 'décimales oui'
        : `décimales max ${maxDecimals}`
      : 'entier';
    if (!hasMin && !hasMax) return decimalPart;
    const range = hasMin && hasMax ? `${min}..${max}` : hasMin ? `min ${min}` : `max ${max}`;
    return `${range} • ${decimalPart}`;
  })();

  return (
    <div
      className={`ScriptsBlock ${isDragging ? 'is-dragging' : ''}`}
      style={{ '--block-color': meta.color || '#94a3b8' }}
    >
      <div className="ScriptsBlockHead">
        <div className="ButtonInline" style={{ gap: 8, minWidth: 0 }}>
          <button
            type="button"
            className="ScriptsDragHandle"
            title="Déplacer"
            draggable
            onDragStart={(e) => onDragStart?.(e, block)}
            onDragEnd={() => onDragEnd?.()}
          >
            ⋮⋮
          </button>
          <span className="ScriptsBlockIcon" aria-hidden="true">
            <Icon width={16} height={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="LabelStrong OneLine">{meta.label}</div>
            <div className="Muted OneLine">{meta.hint}</div>
          </div>
        </div>
        <div className="Badges">
          <button
            type="button"
            className="Button Button--ghost Button--icon"
            title="Monter"
            disabled={index <= 0}
            onClick={() => onMove?.(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="Button Button--ghost Button--icon"
            title="Descendre"
            disabled={index >= total - 1}
            onClick={() => onMove?.(+1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="Button Button--ghost Button--icon"
            title="Dupliquer"
            onClick={onDuplicate}
          >
            <IconCopy />
          </button>
          <button
            type="button"
            className="Button Button--ghost Button--danger Button--icon"
            title="Supprimer"
            onClick={onDelete}
          >
            <IconTrash />
          </button>
        </div>
      </div>

      {meta.comingSoon ? (
        <div className="Hint" style={{ marginTop: 10 }}>
          Action en préparation. Tu peux la garder dans le workflow, l’exécution sera ignorée pour le moment.
        </div>
      ) : null}

      {block.type === 'set_var' ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Nom de variable</label>
            <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Valeur</label>
            <input className="Input" value={String(block.value ?? '')} onChange={(e) => onPatch({ value: e.target.value })} />
          </div>
        </div>
      ) : null}

      {block.type === 'template' ? (
        <>
          <div className="FormGrid" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Nom de variable</label>
              <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Template</label>
            <VariableTextarea
              value={String(block.template || '')}
              onChange={(next) => onPatch({ template: next })}
              rows={4}
              placeholder="Bonjour [[first_name]]"
              variables={variableSuggestions}
            />
          </div>
        </>
      ) : null}

      {block.type === 'ask_text' ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Variable réponse</label>
            <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Question</label>
            <input className="Input" value={String(block.label || '')} onChange={(e) => onPatch({ label: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Placeholder</label>
            <input className="Input" value={String(block.placeholder || '')} onChange={(e) => onPatch({ placeholder: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Valeur par défaut</label>
            <input className="Input" value={String(block.default || '')} onChange={(e) => onPatch({ default: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Obligatoire</label>
            <button type="button" className={`TogglePill ${Boolean(block.required) ? 'is-on' : ''}`} onClick={() => onPatch({ required: !Boolean(block.required) })}>
              {Boolean(block.required) ? 'Oui' : 'Non'}
            </button>
          </div>
          <div className="Field">
            <label className="Label">Conditions</label>
            <button type="button" className="Button Button--ghost" onClick={() => setConditionsOpen(true)}>
              {textCondLabel}
            </button>
          </div>
        </div>
      ) : null}

      {block.type === 'ask_number' ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Variable réponse</label>
            <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Question</label>
            <input className="Input" value={String(block.label || '')} onChange={(e) => onPatch({ label: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Valeur par défaut</label>
            <input className="Input" value={String(block.default ?? 0)} onChange={(e) => onPatch({ default: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Obligatoire</label>
            <button type="button" className={`TogglePill ${Boolean(block.required) ? 'is-on' : ''}`} onClick={() => onPatch({ required: !Boolean(block.required) })}>
              {Boolean(block.required) ? 'Oui' : 'Non'}
            </button>
          </div>
          <div className="Field">
            <label className="Label">Conditions</label>
            <button type="button" className="Button Button--ghost" onClick={() => setConditionsOpen(true)}>
              {numberCondLabel}
            </button>
          </div>
        </div>
      ) : null}

      {block.type === 'ask_select' ? (
        <>
          <div className="FormGrid" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Variable réponse</label>
              <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
            </div>
            <div className="Field">
              <label className="Label">Question</label>
              <input className="Input" value={String(block.label || '')} onChange={(e) => onPatch({ label: e.target.value })} />
            </div>
            <div className="Field">
              <label className="Label">Multisélection</label>
              <button type="button" className={`TogglePill ${Boolean(block.multiple) ? 'is-on' : ''}`} onClick={() => onPatch({ multiple: !Boolean(block.multiple) })}>
                {Boolean(block.multiple) ? 'Oui' : 'Non'}
              </button>
            </div>
            <div className="Field">
              <label className="Label">Obligatoire</label>
              <button type="button" className={`TogglePill ${Boolean(block.required) ? 'is-on' : ''}`} onClick={() => onPatch({ required: !Boolean(block.required) })}>
                {Boolean(block.required) ? 'Oui' : 'Non'}
              </button>
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Options (1 ligne = 1 choix)</label>
            <textarea
              className="Textarea"
              rows={5}
              value={Array.isArray(block.options) ? block.options.join('\n') : ''}
              onChange={(e) =>
                onPatch({
                  options: String(e.target.value || '')
                    .split(/\r?\n/)
                    .map((x) => String(x || '').trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
        </>
      ) : null}

      {block.type === 'ask_confirm' ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Variable réponse</label>
            <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Question</label>
            <input className="Input" value={String(block.label || '')} onChange={(e) => onPatch({ label: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Par défaut</label>
            <button type="button" className={`TogglePill ${Boolean(block.default) ? 'is-on' : ''}`} onClick={() => onPatch({ default: !Boolean(block.default) })}>
              {Boolean(block.default) ? 'Oui' : 'Non'}
            </button>
          </div>
        </div>
      ) : null}

      {block.type === 'date_now' ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Nom de variable</label>
            <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Format</label>
            <select className="Select" value={String(block.format || 'datetime')} onChange={(e) => onPatch({ format: e.target.value })}>
              <option value="iso">ISO (UTC)</option>
              <option value="datetime">YYYY-MM-DD HH:mm:ss</option>
              <option value="date">YYYY-MM-DD</option>
              <option value="timestamp">Timestamp</option>
            </select>
          </div>
        </div>
      ) : null}

      {block.type === 'math' ? (
        <div className="FormRow3" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Variable résultat</label>
            <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Opération</label>
            <select className="Select" value={String(block.op || 'add')} onChange={(e) => onPatch({ op: e.target.value })}>
              <option value="add">Addition</option>
              <option value="sub">Soustraction</option>
              <option value="mul">Multiplication</option>
              <option value="div">Division</option>
            </select>
          </div>
          <div className="Field">
            <label className="Label">Gauche</label>
            <input className="Input" value={String(block.left ?? '')} onChange={(e) => onPatch({ left: e.target.value })} />
          </div>
          <div className="Field">
            <label className="Label">Droite</label>
            <input className="Input" value={String(block.right ?? '')} onChange={(e) => onPatch({ right: e.target.value })} />
          </div>
        </div>
      ) : null}

      {block.type === 'random_uuid' ? (
        <div className="Field" style={{ marginTop: 10 }}>
          <label className="Label">Nom de variable</label>
          <input className="Input" value={String(block.key || '')} onChange={(e) => onPatch({ key: e.target.value })} />
        </div>
      ) : null}

      {block.type === 'http_request' ? (
        <>
          <div className="FormRow3" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Méthode</label>
              <select className="Select" value={String(block.method || 'GET')} onChange={(e) => onPatch({ method: e.target.value })}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="Field" style={{ gridColumn: 'span 2' }}>
              <label className="Label">URL</label>
              <input className="Input" value={String(block.url || '')} onChange={(e) => onPatch({ url: e.target.value })} placeholder="https://api.example.com/data" />
            </div>
            <div className="Field">
              <label className="Label">Variable résultat</label>
              <input className="Input" value={String(block.save_as || '')} onChange={(e) => onPatch({ save_as: e.target.value })} />
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Headers JSON</label>
            <textarea
              className="Textarea"
              rows={3}
              value={typeof block.headers === 'string' ? block.headers : prettyJson(block.headers || {})}
              onChange={(e) => patchHeadersFromText(e.target.value)}
              placeholder='{"Authorization":"Bearer {{token}}"}'
            />
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Body</label>
            <VariableTextarea
              value={String(block.body || '')}
              onChange={(next) => onPatch({ body: next })}
              rows={4}
              variables={variableSuggestions}
            />
          </div>
        </>
      ) : null}

      {block.type === 'json_extract' ? (
        <div className="FormRow3" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Depuis</label>
            <input className="Input" value={String(block.from || '')} onChange={(e) => onPatch({ from: e.target.value })} placeholder="http_response.body_json" />
          </div>
          <div className="Field">
            <label className="Label">Chemin (optionnel)</label>
            <input className="Input" value={String(block.path || '')} onChange={(e) => onPatch({ path: e.target.value })} placeholder="data.0.id" />
          </div>
          <div className="Field">
            <label className="Label">Variable résultat</label>
            <input className="Input" value={String(block.save_as || '')} onChange={(e) => onPatch({ save_as: e.target.value })} />
          </div>
        </div>
      ) : null}

      {block.type === 'create_file' ? (
        <>
          <div className="FormRow3" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Nom du fichier</label>
              <input className="Input" value={String(block.filename || '')} onChange={(e) => onPatch({ filename: e.target.value })} />
            </div>
            <div className="Field">
              <label className="Label">MIME</label>
              <input className="Input" value={String(block.mime || '')} onChange={(e) => onPatch({ mime: e.target.value })} />
            </div>
            <div className="Field">
              <label className="Label">Variable résultat</label>
              <input className="Input" value={String(block.save_as || '')} onChange={(e) => onPatch({ save_as: e.target.value })} />
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Contenu</label>
            <VariableTextarea
              value={String(block.template || '')}
              onChange={(next) => onPatch({ template: next })}
              rows={4}
              variables={variableSuggestions}
            />
          </div>
        </>
      ) : null}

      {block.type === 'show' ? (
        <>
          <div className="FormRow3" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Titre</label>
              <input className="Input" value={String(block.title || '')} onChange={(e) => onPatch({ title: e.target.value })} />
            </div>
            <div className="Field">
              <label className="Label">Valeur depuis variable (optionnel)</label>
              <input className="Input" value={String(block.from || '')} onChange={(e) => onPatch({ from: e.target.value })} placeholder="project_description" />
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Texte affiché (prioritaire si renseigné)</label>
            <VariableTextarea
              value={String(block.template || '')}
              onChange={(next) => onPatch({ template: next })}
              rows={4}
              variables={variableSuggestions}
            />
          </div>
        </>
      ) : null}

      {block.type === 'notify' ? (
        <div className="Field" style={{ marginTop: 10 }}>
          <label className="Label">Message</label>
          <input className="Input" value={String(block.message || '')} onChange={(e) => onPatch({ message: e.target.value })} />
        </div>
      ) : null}

      {block.type === 'list_projects' || block.type === 'list_resources' || block.type === 'list_contacts' ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Limite</label>
            <input
              className="Input"
              type="number"
              min={1}
              max={500}
              value={String(block.limit ?? 100)}
              onChange={(e) => onPatch({ limit: coerceNumber(e.target.value, 100) })}
            />
          </div>
          {block.type === 'list_resources' ? (
            <div className="Field">
              <label className="Label">Inclure masqués</label>
              <button
                type="button"
                className={`TogglePill ${Boolean(block.include_hidden) ? 'is-on' : ''}`}
                onClick={() => onPatch({ include_hidden: !Boolean(block.include_hidden) })}
              >
                {Boolean(block.include_hidden) ? 'Oui' : 'Non'}
              </button>
            </div>
          ) : null}
          <div className="Field">
            <label className="Label">Variable résultat</label>
            <input className="Input" value={String(block.save_as || '')} onChange={(e) => onPatch({ save_as: e.target.value })} />
          </div>
        </div>
      ) : null}

      {block.type === 'create_update' ? (
        <>
          <div className="FormRow3" style={{ marginTop: 10 }}>
            <div className="Field" style={{ gridColumn: 'span 2' }}>
              <label className="Label">Titre</label>
              <input className="Input" value={String(block.title || '')} onChange={(e) => onPatch({ title: e.target.value })} />
            </div>
            <div className="Field">
              <label className="Label">Niveau</label>
              <select className="Select" value={String(block.level || 'minor')} onChange={(e) => onPatch({ level: e.target.value })}>
                <option value="minor">Mineur</option>
                <option value="major">Majeur</option>
              </select>
            </div>
            <div className="Field" style={{ gridColumn: 'span 3' }}>
              <label className="Label">Variable résultat</label>
              <input className="Input" value={String(block.save_as || '')} onChange={(e) => onPatch({ save_as: e.target.value })} />
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Changements (1 ligne = 1 changement)</label>
            <VariableTextarea
              value={String(block.changes || '')}
              onChange={(next) => onPatch({ changes: next })}
              rows={5}
              variables={variableSuggestions}
            />
          </div>
        </>
      ) : null}

      {conditionsOpen && block.type === 'ask_text' ? (
        <Modal
          title="Conditions texte"
          icon={<IconTag />}
          onClose={() => setConditionsOpen(false)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setConditionsOpen(false)}>
              Fermer
            </button>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Longueur minimale</label>
              <input
                className="Input"
                type="number"
                min={0}
                value={String(block.min_length ?? '')}
                onChange={(e) => onPatch({ min_length: e.target.value })}
                placeholder="Aucune"
              />
            </div>
            <div className="Field">
              <label className="Label">Longueur maximale</label>
              <input
                className="Input"
                type="number"
                min={0}
                value={String(block.max_length ?? '')}
                onChange={(e) => onPatch({ max_length: e.target.value })}
                placeholder="Aucune"
              />
            </div>
          </div>
        </Modal>
      ) : null}

      {conditionsOpen && block.type === 'ask_number' ? (
        <Modal
          title="Conditions nombre"
          icon={<IconTag />}
          onClose={() => setConditionsOpen(false)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setConditionsOpen(false)}>
              Fermer
            </button>
          }
        >
          <div className="FormRow3">
            <div className="Field">
              <label className="Label">Minimum</label>
              <input
                className="Input"
                type="number"
                value={String(block.min ?? '')}
                onChange={(e) => onPatch({ min: e.target.value })}
                placeholder="Aucun"
              />
            </div>
            <div className="Field">
              <label className="Label">Maximum</label>
              <input
                className="Input"
                type="number"
                value={String(block.max ?? '')}
                onChange={(e) => onPatch({ max: e.target.value })}
                placeholder="Aucun"
              />
            </div>
          </div>
          <div className="FormRow3" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Décimales autorisées</label>
              <button
                type="button"
                className={`TogglePill ${block.allow_decimals == null || Boolean(block.allow_decimals) ? 'is-on' : ''}`}
                onClick={() =>
                  onPatch({
                    allow_decimals: !(block.allow_decimals == null || Boolean(block.allow_decimals)),
                  })
                }
              >
                {block.allow_decimals == null || Boolean(block.allow_decimals) ? 'Oui' : 'Non'}
              </button>
            </div>
            <div className="Field">
              <label className="Label">Nombre max de décimales</label>
              <input
                className="Input"
                type="number"
                min={0}
                value={String(block.max_decimals ?? '')}
                onChange={(e) => onPatch({ max_decimals: e.target.value })}
                disabled={!(block.allow_decimals == null || Boolean(block.allow_decimals))}
                placeholder="Aucune limite"
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export default function ScriptsPage({ siteId }) {
  const fileRef = useRef(null);
  const createFileRef = useRef(null);

  const [state, setState] = useState({ status: 'loading', error: '', scripts: [], runs: [] });
  const [enabledModuleKeys, setEnabledModuleKeys] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runInputText, setRunInputText] = useState('{}');
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState('');
  const [runPrompt, setRunPrompt] = useState(null);
  const [runDisplay, setRunDisplay] = useState(null);
  const [hostDraft, setHostDraft] = useState('');
  const [modal, setModal] = useState(null);
  const [screenMode, setScreenMode] = useState('list');
  const [editTab, setEditTab] = useState('config');
  const [dragCatalogType, setDragCatalogType] = useState('');
  const [dragBlockId, setDragBlockId] = useState('');
  const [dragOverIndex, setDragOverIndex] = useState(-1);

  const refresh = useCallback(async () => {
    if (!siteId) return;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    const [res, modRes] = await Promise.all([
      api.scriptsList(Number(siteId), {
        limit: 500,
        include_runs: true,
        run_limit: 80,
      }),
      api.modules(Number(siteId)),
    ]);
    if (modRes?.ok) {
      const keys = (Array.isArray(modRes.modules) ? modRes.modules : [])
        .map((m) => String(m?.key || '').trim())
        .filter(Boolean);
      setEnabledModuleKeys(keys);
    } else {
      setEnabledModuleKeys([]);
    }
    if (!res.ok) {
      setState({ status: 'error', error: res.error || 'Chargement impossible', scripts: [], runs: [] });
      return;
    }
    const scripts = Array.isArray(res.scripts) ? res.scripts : [];
    const runs = Array.isArray(res.runs) ? res.runs : [];
    setState({ status: 'ready', error: '', scripts, runs });
  }, [siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selectedScript = useMemo(() => {
    const id = Number(selectedId || 0);
    return (state.scripts || []).find((x) => Number(x.id) === id) || null;
  }, [state.scripts, selectedId]);

  useEffect(() => {
    if (!Array.isArray(state.scripts) || state.scripts.length === 0) {
      setSelectedId(null);
      setDraft(null);
      setRunResult(null);
      setRunError('');
      setRunPrompt(null);
      setRunDisplay(null);
      setScreenMode('list');
      return;
    }

    const hasSelected = state.scripts.some((x) => Number(x.id) === Number(selectedId || 0));
    const next = hasSelected ? selectedScript : state.scripts[0];
    if (!next) return;

    if (!hasSelected) setSelectedId(Number(next.id));
    setDraft(toDraft(next));
    setRunResult(null);
    setRunError('');
    setRunPrompt(null);
    setRunDisplay(null);
  }, [state.scripts, selectedId, selectedScript]);

  const isDirty = useMemo(() => {
    if (!draft || !selectedScript) return false;
    return draftFingerprint(draft) !== draftFingerprint(toDraft(selectedScript));
  }, [draft, selectedScript]);

  const scriptRuns = useMemo(() => {
    const id = Number(selectedId || 0);
    return (state.runs || []).filter((r) => Number(r.script_id) === id);
  }, [state.runs, selectedId]);

  const variableSuggestions = useMemo(() => {
    const set = new Set([
      'site.id',
      'site.name',
      'workflow.title',
    ]);
    const blocks = Array.isArray(draft?.workflow?.blocks) ? draft.workflow.blocks : [];
    for (const b of blocks) {
      if (!b || typeof b !== 'object') continue;
      const key = String(b.key || '').trim();
      const saveAs = String(b.save_as || '').trim();
      if (key) set.add(key);
      if (saveAs) set.add(saveAs);
    }
    try {
      const rawInput = JSON.parse(String(runInputText || '{}'));
      if (rawInput && typeof rawInput === 'object' && !Array.isArray(rawInput)) {
        Object.keys(rawInput).forEach((k) => set.add(String(k)));
      }
    } catch {
      // ignore
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [draft?.workflow?.blocks, runInputText]);

  const actionsByCategory = useMemo(() => {
    const allowedByModule = new Set(
      (enabledModuleKeys || []).length
        ? enabledModuleKeys
        : [
            'projects',
            'menus',
            'resources',
            'clients',
            'updates',
            'appointments',
            'gallery',
            'traffic',
            'scripts',
          ]
    );
    const map = new Map();
    for (const it of BLOCK_LIBRARY) {
      const moduleKey = String(it?.moduleKey || '').trim();
      if (moduleKey && !allowedByModule.has(moduleKey)) continue;
      const cat = String(it.category || 'Autres');
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(it);
    }
    return Array.from(map.entries()).map(([category, items]) => {
      const moduleKeys = Array.from(
        new Set(
          (items || [])
            .map((it) => String(it?.moduleKey || '').trim())
            .filter(Boolean)
        )
      );
      const moduleKey = moduleKeys.length === 1 ? moduleKeys[0] : '';
      return { category, items, moduleKey };
    });
  }, [enabledModuleKeys]);

  function insertBlockAt(type, atIndex) {
    const t = String(type || '').trim();
    if (!BLOCK_META_BY_TYPE[t]) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      const blocks = [...(wf.blocks || [])];
      const maxBlocks = Number(cur.permissions?.max_blocks || 100) || 100;
      if (blocks.length >= maxBlocks) return cur;
      let idx = Number(atIndex);
      if (!Number.isFinite(idx)) idx = blocks.length;
      idx = Math.max(0, Math.min(blocks.length, idx));
      blocks.splice(idx, 0, defaultBlock(t));
      return { ...cur, workflow: { ...wf, blocks } };
    });
  }

  function moveBlockToIndex(blockId, atIndex) {
    const id = String(blockId || '');
    if (!id) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      const blocks = [...(wf.blocks || [])];
      const from = blocks.findIndex((b) => String(b.id) === id);
      if (from < 0) return cur;
      let idx = Number(atIndex);
      if (!Number.isFinite(idx)) return cur;
      idx = Math.max(0, Math.min(blocks.length, idx));
      const [item] = blocks.splice(from, 1);
      if (from < idx) idx -= 1;
      blocks.splice(idx, 0, item);
      return { ...cur, workflow: { ...wf, blocks } };
    });
  }

  function onDropTimeline(e, index) {
    e.preventDefault();
    setDragOverIndex(-1);
    const catalogType = String(e.dataTransfer?.getData('application/x-scripts-catalog') || dragCatalogType || '').trim();
    if (catalogType) {
      insertBlockAt(catalogType, index);
      setDragCatalogType('');
      setDragBlockId('');
      return;
    }
    const movedBlockId = String(e.dataTransfer?.getData('application/x-scripts-block') || dragBlockId || '').trim();
    if (movedBlockId) {
      moveBlockToIndex(movedBlockId, index);
      setDragBlockId('');
      setDragCatalogType('');
    }
  }

  function onDropOnBlock(e, index) {
    e.preventDefault();
    e.stopPropagation();
    // Drop directly on a block inserts just below it.
    onDropTimeline(e, Number(index) + 1);
  }

  function clearDragState() {
    setDragCatalogType('');
    setDragBlockId('');
    setDragOverIndex(-1);
  }

  function openScript(script, mode = null) {
    if (!script) return;
    setSelectedId(Number(script.id));
    setDraft(toDraft(script));
    setRunResult(null);
    setRunError('');
    if (mode) {
      setScreenMode(mode);
      if (mode === 'edit') setEditTab('config');
    }
  }

  async function executeRunRequest({ scriptId, input, allowSaveDraft }) {
    if (!siteId) return;
    const sid = Number(scriptId || 0);
    if (!Number.isFinite(sid) || sid <= 0) return;

    if (allowSaveDraft && draft && Number(draft.id || 0) === sid && isDirty) {
      const ok = await saveDraft();
      if (!ok) {
        setRunError('Sauvegarde requise avant exécution.');
        return;
      }
    }

    setRunning(true);
    setRunError('');
    setRunResult(null);
    setRunDisplay(null);
    const res = await api.scriptsRun({
      site_id: Number(siteId),
      script_id: sid,
      input: input && typeof input === 'object' && !Array.isArray(input) ? input : {},
    });
    setRunning(false);

    if (!res.ok) {
      setRunError(res.error || 'Exécution impossible');
      setRunResult(res.output ? { run: res.run || null, output: res.output } : null);
      if (Array.isArray(res.output?.displays) && res.output.displays.length > 0) {
        setRunDisplay({ items: res.output.displays, index: 0 });
      }
      return;
    }

    setRunResult({ run: res.run || null, output: res.output || null });
    if (Array.isArray(res.output?.displays) && res.output.displays.length > 0) {
      setRunDisplay({ items: res.output.displays, index: 0 });
    }
    setState((s) => {
      const runs = [
        ...(res.run ? [{ ...res.run, script_id: sid }] : []),
        ...(Array.isArray(s.runs) ? s.runs : []),
      ].slice(0, 120);
      const scripts = (s.scripts || []).map((x) =>
        Number(x.id) === sid
          ? {
              ...x,
              last_run_at: res.run?.finished_at || res.run?.started_at || x.last_run_at || null,
            }
          : x
      );
      return { ...s, runs, scripts };
    });
  }

  function openPromptForRun({ scriptId, steps, input, allowSaveDraft }) {
    const values = { ...(input || {}) };
    for (const step of steps) {
      values[step.key] = step.value;
    }
    setRunPrompt({
      scriptId: Number(scriptId || 0),
      steps,
      index: 0,
      values,
      error: '',
      allowSaveDraft: Boolean(allowSaveDraft),
    });
  }

  async function startRunFlow({ scriptId, workflow, allowSaveDraft }) {
    const parsed = parseRunInputObject(runInputText);
    if (!parsed.ok) {
      setRunError(parsed.error || 'Entrée JSON invalide.');
      return;
    }
    const baseInput = parsed.input || {};
    const steps = buildPromptSteps(workflow?.blocks || [], baseInput);
    if (steps.length > 0) {
      setRunError('');
      setRunResult(null);
      openPromptForRun({
        scriptId,
        steps,
        input: baseInput,
        allowSaveDraft: Boolean(allowSaveDraft),
      });
      return;
    }
    await executeRunRequest({
      scriptId,
      input: baseInput,
      allowSaveDraft: Boolean(allowSaveDraft),
    });
  }

  async function quickRunScript(script) {
    if (!script || !siteId) return;
    const sid = Number(script.id || 0);
    if (!Number.isFinite(sid) || sid <= 0) return;
    openScript(script, 'play');
    await startRunFlow({
      scriptId: sid,
      workflow: normalizeWorkflow(script.workflow, script.permissions),
      allowSaveDraft: Number(draft?.id || 0) === sid && Number(selectedId || 0) === sid,
    });
  }

  function patchDraft(patch) {
    setDraft((d) => (d ? { ...d, ...(patch || {}) } : d));
  }

  function patchWorkflow(patch) {
    setDraft((d) => {
      if (!d) return d;
      const wf = d.workflow || normalizeWorkflow(null, d.permissions);
      return { ...d, workflow: { ...wf, ...(patch || {}) } };
    });
  }

  function patchPermissions(patch) {
    setDraft((d) => {
      if (!d) return d;
      const nextPermissions = normalizePermissions({ ...(d.permissions || {}), ...(patch || {}) });
      const nextWorkflow = normalizeWorkflow(d.workflow, nextPermissions);
      return {
        ...d,
        permissions: nextPermissions,
        workflow: nextWorkflow,
      };
    });
  }

  function patchBlock(blockId, patch) {
    const id = String(blockId || '');
    if (!id) return;
    setDraft((d) => {
      if (!d) return d;
      const wf = d.workflow || normalizeWorkflow(null, d.permissions);
      const blocks = (wf.blocks || []).map((b) => (String(b.id) === id ? normalizeBlock({ ...b, ...(patch || {}) }) : b));
      return { ...d, workflow: { ...wf, blocks } };
    });
  }

  function moveBlock(blockId, delta) {
    const id = String(blockId || '');
    const d = Number(delta);
    if (!id || !Number.isFinite(d) || !d) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      const list = [...(wf.blocks || [])];
      const idx = list.findIndex((b) => String(b.id) === id);
      if (idx < 0) return cur;
      const to = idx + d;
      if (to < 0 || to >= list.length) return cur;
      const [item] = list.splice(idx, 1);
      list.splice(to, 0, item);
      return { ...cur, workflow: { ...wf, blocks: list } };
    });
  }

  function duplicateBlock(blockId) {
    const id = String(blockId || '');
    if (!id) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      const list = [...(wf.blocks || [])];
      const idx = list.findIndex((b) => String(b.id) === id);
      if (idx < 0) return cur;
      const copy = deepClone(list[idx]);
      copy.id = newBlockId();
      list.splice(idx + 1, 0, normalizeBlock(copy));
      return { ...cur, workflow: { ...wf, blocks: list } };
    });
  }

  function removeBlock(blockId) {
    const id = String(blockId || '');
    if (!id) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      return { ...cur, workflow: { ...wf, blocks: (wf.blocks || []).filter((b) => String(b.id) !== id) } };
    });
  }

  function addAllowedHost() {
    let host = String(hostDraft || '').trim().toLowerCase();
    if (!host) return;
    host = host.replace(/^https?:\/\//i, '');
    host = host.replace(/\/.*$/, '');
    if (!/^[a-z0-9.-]+$/.test(host)) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      const list = Array.isArray(wf.network?.allowed_hosts) ? [...wf.network.allowed_hosts] : [];
      if (!list.includes(host)) list.push(host);
      return {
        ...cur,
        workflow: {
          ...wf,
          network: {
            ...(wf.network || {}),
            allowed_hosts: list.slice(0, 50),
          },
        },
      };
    });
    setHostDraft('');
  }

  function removeAllowedHost(host) {
    const h = String(host || '').trim().toLowerCase();
    if (!h) return;
    setDraft((cur) => {
      if (!cur) return cur;
      const wf = cur.workflow || normalizeWorkflow(null, cur.permissions);
      const list = (wf.network?.allowed_hosts || []).filter((x) => String(x || '').toLowerCase() !== h);
      return {
        ...cur,
        workflow: {
          ...wf,
          network: {
            ...(wf.network || {}),
            allowed_hosts: list,
          },
        },
      };
    });
  }

  async function createScript({
    name = '',
    sourceJs = '',
    workflow = null,
  } = {}) {
    if (!siteId) return;
    const idx = (state.scripts || []).length + 1;
    const resolvedName = String(name || '').trim() || `Script ${idx}`;
    const permissions = defaultPermissions();
    const nextWorkflow = normalizeWorkflow(
      workflow || {
        title: resolvedName,
        description: '',
        network: { allowed_hosts: [] },
        blocks: [defaultBlock('notify')],
      },
      permissions
    );
    const source = String(sourceJs || '').trim() || JSON.stringify(nextWorkflow, null, 2);

    const res = await api.scriptsCreate({
      site_id: Number(siteId),
      name: resolvedName,
      source_js: source,
      workflow: nextWorkflow,
      permissions,
      is_enabled: true,
      sort_order: (state.scripts || []).length,
      compiled: {
        parser: 'scriptsWorkflow.v1',
        parsed_at: new Date().toISOString(),
        blocks: nextWorkflow.blocks.length,
      },
    });
    if (!res.ok) {
      setState((s) => ({ ...s, error: res.error || 'Création impossible' }));
      return false;
    }
    const created = res.script || null;
    if (!created) {
      await refresh();
      return false;
    }
    setState((s) => ({ ...s, scripts: [created, ...(s.scripts || [])] }));
    setSelectedId(Number(created.id));
    setDraft(toDraft(created));
    setRunResult(null);
    setRunError('');
    setScreenMode('edit');
    setEditTab('config');
    return true;
  }

  function openCreateScriptModal() {
    setModal({
      type: 'new-script',
      mode: 'create',
      name: '',
      source_js: '',
      imported_filename: '',
      error: '',
      saving: false,
    });
  }

  function pickNewScriptFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || '');
      setModal((m) =>
        m?.type === 'new-script'
          ? {
              ...m,
              imported_filename: String(file.name || 'script.js'),
              source_js: content,
              error: '',
            }
          : m
      );
    };
    reader.onerror = () => {
      setModal((m) =>
        m?.type === 'new-script'
          ? {
              ...m,
              error: 'Lecture du fichier impossible',
            }
          : m
      );
    };
    reader.readAsText(file);
  }

  async function submitCreateScriptModal() {
    const m = modal;
    if (!m || m.type !== 'new-script' || m.saving) return;

    if (m.mode === 'create') {
      setModal((cur) => (cur?.type === 'new-script' ? { ...cur, saving: true, error: '' } : cur));
      const ok = await createScript({ name: m.name });
      if (!ok) {
        setModal((cur) => (cur?.type === 'new-script' ? { ...cur, saving: false, error: 'Création impossible' } : cur));
        return;
      }
      setModal(null);
      return;
    }

    const source = String(m.source_js || '').trim();
    if (!source) {
      setModal((cur) => (cur?.type === 'new-script' ? { ...cur, error: 'Sélectionne d’abord un fichier JS à importer.' } : cur));
      return;
    }

    setModal((cur) => (cur?.type === 'new-script' ? { ...cur, saving: true, error: '' } : cur));
    const compiled = await api.scriptsCompile({
      site_id: Number(siteId),
      source_js: source,
    });
    if (!compiled.ok) {
      setModal((cur) =>
        cur?.type === 'new-script'
          ? { ...cur, saving: false, error: compiled.error || 'Conversion JS impossible' }
          : cur
      );
      return;
    }

    const fallbackName =
      String(m.name || '').trim() ||
      String(compiled.workflow?.title || '').trim() ||
      String(m.imported_filename || '').replace(/\.[^.]+$/, '').trim();
    const ok = await createScript({
      name: fallbackName,
      sourceJs: String(compiled.source_js || source),
      workflow: compiled.workflow || null,
    });
    if (!ok) {
      setModal((cur) => (cur?.type === 'new-script' ? { ...cur, saving: false, error: 'Création impossible' } : cur));
      return;
    }
    setModal(null);
  }

  async function saveDraft() {
    if (!siteId || !draft) return false;
    const id = Number(draft.id || 0);
    if (!Number.isFinite(id) || id <= 0) return false;

    setSaving(true);
    const workflow = normalizeWorkflow(draft.workflow, draft.permissions);
    const permissions = normalizePermissions(draft.permissions);

    const res = await api.scriptsUpdate({
      site_id: Number(siteId),
      id,
      name: String(draft.name || '').trim() || 'Script',
      source_js: String(draft.source_js || '').trim() || JSON.stringify(workflow, null, 2),
      workflow,
      permissions,
      is_enabled: Boolean(draft.is_enabled),
      sort_order: Number(draft.sort_order || 0),
      compiled: {
        parser: 'scriptsWorkflow.v1',
        parsed_at: new Date().toISOString(),
        blocks: (workflow.blocks || []).length,
      },
    });
    setSaving(false);

    if (!res.ok) {
      setState((s) => ({ ...s, error: res.error || 'Enregistrement impossible' }));
      return false;
    }

    const updated = res.script || null;
    if (!updated) {
      await refresh();
      return true;
    }

    setState((s) => ({
      ...s,
      scripts: (s.scripts || []).map((x) => (Number(x.id) === Number(updated.id) ? updated : x)),
    }));
    setDraft(toDraft(updated));
    return true;
  }

  async function runSelectedScript() {
    if (!draft || !siteId) return;
    const scriptId = Number(draft.id || 0);
    if (!Number.isFinite(scriptId) || scriptId <= 0) return;
    await startRunFlow({
      scriptId,
      workflow: draft.workflow || { blocks: [] },
      allowSaveDraft: true,
    });
  }

  async function clearScriptRuns() {
    if (!siteId || !selectedScript) return;
    const sid = Number(selectedScript.id || 0);
    if (!Number.isFinite(sid) || sid <= 0) return;
    const res = await api.scriptsRunsClear({
      site_id: Number(siteId),
      script_id: sid,
    });
    if (!res.ok) {
      setRunError(res.error || 'Suppression de l’historique impossible');
      return;
    }
    setState((s) => ({
      ...s,
      runs: (s.runs || []).filter((r) => Number(r.script_id) !== sid),
      scripts: (s.scripts || []).map((x) =>
        Number(x.id) === sid ? { ...x, last_run_at: null } : x
      ),
    }));
    setRunResult(null);
  }

  const currentPromptStep = useMemo(() => {
    if (!runPrompt || !Array.isArray(runPrompt.steps)) return null;
    const idx = Math.max(0, Math.min(runPrompt.steps.length - 1, Number(runPrompt.index || 0)));
    return runPrompt.steps[idx] || null;
  }, [runPrompt]);

  function patchPromptValue(nextValue) {
    setRunPrompt((cur) => {
      if (!cur || !Array.isArray(cur.steps) || !cur.steps.length) return cur;
      const idx = Math.max(0, Math.min(cur.steps.length - 1, Number(cur.index || 0)));
      const step = cur.steps[idx];
      if (!step?.key) return cur;
      return {
        ...cur,
        values: {
          ...(cur.values || {}),
          [step.key]: nextValue,
        },
        error: '',
      };
    });
  }

  async function submitPromptStep() {
    if (!runPrompt || !Array.isArray(runPrompt.steps) || !runPrompt.steps.length) return;
    const idx = Math.max(0, Math.min(runPrompt.steps.length - 1, Number(runPrompt.index || 0)));
    const step = runPrompt.steps[idx];
    if (!step) return;
    const values = runPrompt.values && typeof runPrompt.values === 'object' ? runPrompt.values : {};
    const value = values[step.key];
    if (!promptValueIsValid(step, value)) {
      setRunPrompt((cur) => (cur ? { ...cur, error: promptValidationMessage(step, value) } : cur));
      return;
    }
    if (idx < runPrompt.steps.length - 1) {
      setRunPrompt((cur) => (cur ? { ...cur, index: idx + 1, error: '' } : cur));
      return;
    }
    const finalInput = { ...(values || {}) };
    const scriptId = Number(runPrompt.scriptId || 0);
    const allowSaveDraft = Boolean(runPrompt.allowSaveDraft);
    setRunPrompt(null);
    await executeRunRequest({
      scriptId,
      input: finalInput,
      allowSaveDraft,
    });
  }

  function previousPromptStep() {
    setRunPrompt((cur) => {
      if (!cur) return cur;
      const idx = Math.max(0, Number(cur.index || 0) - 1);
      return { ...cur, index: idx, error: '' };
    });
  }

  async function deleteSelected() {
    if (!selectedScript || !siteId) return;
    const id = Number(selectedScript.id || 0);
    if (!Number.isFinite(id) || id <= 0) return;

    setModal((m) => ({ ...(m || {}), saving: true, error: '' }));
    const res = await api.scriptsDelete({ site_id: Number(siteId), id });
    if (!res.ok) {
      setModal((m) => ({ ...(m || {}), saving: false, error: res.error || 'Suppression impossible' }));
      return;
    }

    setModal(null);
    setState((s) => ({
      ...s,
      scripts: (s.scripts || []).filter((x) => Number(x.id) !== id),
      runs: (s.runs || []).filter((r) => Number(r.script_id) !== id),
    }));
  }

  function importWorkflowFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const txt = String(reader.result || '');
      let parsed = null;
      try {
        parsed = JSON.parse(txt);
      } catch {
        parsed = null;
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Array.isArray(parsed.blocks)) {
        setDraft((d) => {
          if (!d) return d;
          const permissions = normalizePermissions(d.permissions);
          return {
            ...d,
            source_js: txt,
            workflow: normalizeWorkflow(parsed, permissions),
          };
        });
        return;
      }
      setDraft((d) => (d ? { ...d, source_js: txt } : d));
    };
    reader.readAsText(file);
  }

  if (!siteId) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Scripts</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb
            items={
              screenMode === 'list'
                ? [{ label: 'Scripts' }]
                : [
                    { label: 'Scripts', onClick: () => setScreenMode('list') },
                    { label: draft?.name || `Script #${draft?.id || ''}` },
                  ]
            }
          />
          {screenMode === 'list' ? (
            <div className="Badges">
              <button type="button" className="Button" onClick={openCreateScriptModal}>
                <span className="ButtonInline">
                  <IconPlus /> Nouveau script
                </span>
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 12 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="Card" role="status" aria-live="polite">
          Chargement…
        </div>
      ) : screenMode === 'list' ? (
        <div className="Card">
          <div className="Row" style={{ marginBottom: 10 }}>
            <div className="TileTitle">Raccourcis</div>
          </div>
          <div className="List" role="list">
            {(state.scripts || []).map((s) => {
              const wf = normalizeWorkflow(s.workflow, s.permissions);
              const isActive = Number(s.id) === Number(selectedId || 0);
              return (
                <div key={s.id} className={`ListItem ${isActive ? 'is-active' : ''}`}>
                  <button type="button" className="ScriptsListMain" onClick={() => openScript(s)}>
                    <div style={{ minWidth: 0 }}>
                      <div className="UserName OneLine">{s.name || `Script #${s.id}`}</div>
                      <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 8 }}>
                        <span className="Badge">{s.is_enabled ? 'Actif' : 'Désactivé'}</span>
                        <span className="Badge">{(wf.blocks || []).length} bloc(s)</span>
                        {s.last_run_at ? <span className="Badge">Run: {formatDateTime(s.last_run_at)}</span> : null}
                      </div>
                    </div>
                  </button>
                  <div className="ScriptsListActions">
                    <button
                      type="button"
                      className="Button"
                      onClick={() => quickRunScript(s)}
                      disabled={running || !Boolean(s.is_enabled)}
                    >
                      Play
                    </button>
                    <button type="button" className="Button Button--ghost" onClick={() => openScript(s, 'edit')}>
                      Éditer
                    </button>
                  </div>
                </div>
              );
            })}
            {(state.scripts || []).length === 0 ? <div className="Muted">Aucun script pour ce site.</div> : null}
          </div>
        </div>
      ) : (
        <div className="ScriptsMain">
          {!draft ? (
            <div className="Card">
              <div className="Muted">Sélectionne ou crée un script.</div>
            </div>
          ) : (
            <>
              {screenMode === 'edit' ? (
                <>
                  <div className="Card ScriptsModeCard">
                    <div className="Row" style={{ marginBottom: 0 }}>
                      <div className="TileTitle">Édition — {draft.name || `Script #${draft.id}`}</div>
                      <div className="Badges">
                        <button type="button" className="Button Button--ghost" onClick={() => setScreenMode('list')}>
                          Retour
                        </button>
                        <button type="button" className="Button Button--ghost" onClick={() => setScreenMode('play')}>
                          Play
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="Card">
                    <div className="ScriptsTopNav">
                      {[
                        ['config', 'Configuration'],
                        ['permissions', 'Permissions'],
                        ['actions', 'Actions'],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={`Button ${editTab === key ? '' : 'Button--ghost'}`}
                          onClick={() => setEditTab(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {editTab === 'config' ? (
                    <div className="Card">
                      <div className="Row" style={{ marginBottom: 10 }}>
                        <div className="TileTitle">Configuration</div>
                        <div className="Badges">
                          <input
                            ref={fileRef}
                            type="file"
                            accept=".json,.txt,.js,.mjs,.cjs"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              importWorkflowFile(e.target.files?.[0]);
                              e.target.value = '';
                            }}
                          />
                          <button type="button" className="Button Button--ghost" onClick={() => fileRef.current?.click?.()}>
                            Importer
                          </button>
                          <button
                            type="button"
                            className="Button Button--ghost"
                            onClick={() => {
                              const content = prettyJson(draft.workflow || {});
                              downloadTextFile(
                                `${String(draft.name || 'workflow').replace(/\s+/g, '_').toLowerCase() || 'workflow'}.json`,
                                content,
                                'application/json;charset=utf-8'
                              );
                            }}
                          >
                            <span className="ButtonInline">
                              <IconDownload /> Exporter
                            </span>
                          </button>
                          <button type="button" className="Button" disabled={saving || !isDirty} onClick={saveDraft}>
                            {saving ? 'Enregistrement…' : 'Enregistrer'}
                          </button>
                          <button
                            type="button"
                            className="Button Button--ghost Button--danger Button--icon"
                            title="Supprimer"
                            aria-label="Supprimer"
                            onClick={() => setModal({ type: 'delete-script', saving: false, error: '' })}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </div>

                      <div className="FormGrid">
                        <div className="Field">
                          <label className="Label">Nom du script</label>
                          <input className="Input" value={String(draft.name || '')} onChange={(e) => patchDraft({ name: e.target.value })} />
                        </div>
                        <div className="Field">
                          <label className="Label">Titre affiché</label>
                          <input
                            className="Input"
                            value={String(draft.workflow?.title || '')}
                            onChange={(e) => patchWorkflow({ title: e.target.value })}
                          />
                        </div>
                        <div className="Field">
                          <label className="Label">Statut</label>
                          <button
                            type="button"
                            className={`TogglePill ${draft.is_enabled ? 'is-on' : ''}`}
                            onClick={() => patchDraft({ is_enabled: !Boolean(draft.is_enabled) })}
                          >
                            {draft.is_enabled ? 'Activé' : 'Désactivé'}
                          </button>
                        </div>
                      </div>

                      <div className="Field" style={{ marginTop: 10 }}>
                        <label className="Label">Description</label>
                        <VariableTextarea
                          value={String(draft.workflow?.description || '')}
                          onChange={(next) => patchWorkflow({ description: next })}
                          rows={3}
                          variables={variableSuggestions}
                        />
                      </div>

                      <div className="Notice" style={{ marginTop: 10 }}>
                        <div className="ButtonInline" style={{ gap: 8 }}>
                          <IconAlertTriangle />
                          <strong>Sécurité</strong>
                        </div>
                        <div className="Muted" style={{ marginTop: 6 }}>
                          Exécution strictement contrôlée côté API: types d’actions limités, permissions explicites, quota HTTP,
                          timeout réseau, blocage localhost/plages privées et audit des exécutions.
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {editTab === 'permissions' ? (
                    <div className="Card">
                      <div className="TileTitle" style={{ marginBottom: 10 }}>Permissions</div>
                      <div className="ScriptsToggleGrid">
                        <ToggleLine
                          label="Accès HTTP"
                          description="Autorise les appels API externes"
                          value={Boolean(draft.permissions?.allow_http)}
                          onChange={(v) => patchPermissions({ allow_http: v })}
                        />
                        <ToggleLine
                          label="Génération de fichiers"
                          description="Autorise create_file"
                          value={Boolean(draft.permissions?.allow_generate_files)}
                          onChange={(v) => patchPermissions({ allow_generate_files: v })}
                        />
                        <ToggleLine
                          label="Lire Projets"
                          description="Autorise list_projects"
                          value={Boolean(draft.permissions?.allow_projects_read)}
                          onChange={(v) => patchPermissions({ allow_projects_read: v })}
                        />
                        <ToggleLine
                          label="Lire Ressources"
                          description="Autorise list_resources"
                          value={Boolean(draft.permissions?.allow_resources_read)}
                          onChange={(v) => patchPermissions({ allow_resources_read: v })}
                        />
                        <ToggleLine
                          label="Lire Contacts"
                          description="Autorise list_contacts"
                          value={Boolean(draft.permissions?.allow_contacts_read)}
                          onChange={(v) => patchPermissions({ allow_contacts_read: v })}
                        />
                        <ToggleLine
                          label="Créer des mises à jour"
                          description="Autorise create_update (droits admin module requis)"
                          value={Boolean(draft.permissions?.allow_updates_write)}
                          onChange={(v) => patchPermissions({ allow_updates_write: v })}
                        />
                        <ToggleLine
                          label="Notifications"
                          description="Autorise l’action notify"
                          value={Boolean(draft.permissions?.allow_notify)}
                          onChange={(v) => patchPermissions({ allow_notify: v })}
                        />
                      </div>

                      <div className="FormRow3" style={{ marginTop: 10 }}>
                        <div className="Field">
                          <label className="Label">Quota HTTP</label>
                          <input
                            className="Input"
                            type="number"
                            min={0}
                            max={50}
                            value={String(draft.permissions?.max_http_calls ?? 5)}
                            onChange={(e) => patchPermissions({ max_http_calls: coerceNumber(e.target.value, 5) })}
                          />
                        </div>
                        <div className="Field">
                          <label className="Label">Blocs max</label>
                          <input
                            className="Input"
                            type="number"
                            min={1}
                            max={500}
                            value={String(draft.permissions?.max_blocks ?? 100)}
                            onChange={(e) => patchPermissions({ max_blocks: coerceNumber(e.target.value, 100) })}
                          />
                        </div>
                      </div>

                      <div className="Field" style={{ marginTop: 10 }}>
                        <label className="Label">Allowlist domaines HTTP</label>
                        <div className="InputRow">
                          <input
                            className="Input"
                            value={hostDraft}
                            onChange={(e) => setHostDraft(e.target.value)}
                            placeholder="api.exemple.com"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addAllowedHost();
                              }
                            }}
                            disabled={!Boolean(draft.permissions?.allow_http)}
                          />
                          <button
                            type="button"
                            className="Button Button--ghost"
                            onClick={addAllowedHost}
                            disabled={!Boolean(draft.permissions?.allow_http)}
                          >
                            Ajouter
                          </button>
                        </div>
                        <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 8 }}>
                          {(draft.workflow?.network?.allowed_hosts || []).map((host) => (
                            <button
                              key={host}
                              type="button"
                              className="Badge"
                              onClick={() => removeAllowedHost(host)}
                              title="Retirer"
                            >
                              <span className="ButtonInline" style={{ gap: 6 }}>
                                <IconGlobe width={14} height={14} /> {host}
                              </span>
                            </button>
                          ))}
                          {(draft.workflow?.network?.allowed_hosts || []).length === 0 ? (
                            <span className="Muted">Aucun domaine autorisé.</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {editTab === 'actions' ? (
                    <div className="Card">
                      <div className="Row" style={{ marginBottom: 10 }}>
                        <div className="TileTitle">Actions</div>
                        <div className="Muted">Timeline à gauche, catalogue à droite (drag & drop)</div>
                      </div>

                      <div className="ScriptsActionsBoard">
                        <div className="ScriptsTimelineCol">
                          <div
                            className={`ScriptsDropZone ${dragOverIndex === 0 ? 'is-active' : ''}`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverIndex(0);
                            }}
                            onDragLeave={() => {
                              if (dragOverIndex === 0) setDragOverIndex(-1);
                            }}
                            onDrop={(e) => onDropTimeline(e, 0)}
                          >
                            Insérer
                          </div>
                          {(draft.workflow?.blocks || []).map((b, index) => (
                            <Fragment key={b.id}>
                              <div
                                className={`ScriptsTimelineItem ${dragOverIndex === index + 1 ? 'is-drop-target' : ''}`}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  setDragOverIndex(index + 1);
                                }}
                                onDragLeave={() => {
                                  if (dragOverIndex === index + 1) setDragOverIndex(-1);
                                }}
                                onDrop={(e) => onDropOnBlock(e, index)}
                              >
                                <div className="ScriptsTimelineDot" />
                                <BlockEditor
                                  block={b}
                                  index={index}
                                  total={(draft.workflow?.blocks || []).length}
                                  onPatch={(patch) => patchBlock(b.id, patch)}
                                  onDelete={() => removeBlock(b.id)}
                                  onMove={(delta) => moveBlock(b.id, delta)}
                                  onDuplicate={() => duplicateBlock(b.id)}
                                  onDragStart={(e, block) => {
                                    setDragBlockId(String(block?.id || ''));
                                    setDragCatalogType('');
                                    e.dataTransfer?.setData?.('application/x-scripts-block', String(block?.id || ''));
                                  }}
                                  onDragEnd={clearDragState}
                                  isDragging={String(dragBlockId || '') === String(b.id)}
                                  variableSuggestions={variableSuggestions}
                                />
                              </div>
                              <div
                                className={`ScriptsDropZone ${dragOverIndex === index + 1 ? 'is-active' : ''}`}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  setDragOverIndex(index + 1);
                                }}
                                onDragLeave={() => {
                                  if (dragOverIndex === index + 1) setDragOverIndex(-1);
                                }}
                                onDrop={(e) => onDropTimeline(e, index + 1)}
                              >
                                Insérer
                              </div>
                            </Fragment>
                          ))}
                          {(draft.workflow?.blocks || []).length === 0 ? (
                            <div className="Notice">
                              <div className="Muted">Ajoute ou glisse une action depuis le catalogue.</div>
                            </div>
                          ) : null}
                        </div>

                        <div className="ScriptsCatalogCol">
                          {actionsByCategory.map((group) => (
                            <div key={group.category} className="ScriptsCatalogGroup">
                              <div className="LabelStrong" style={{ marginBottom: 8 }}>
                                {group.moduleKey ? (
                                  <span className="ButtonInline" style={{ gap: 8, color: moduleColor(group.moduleKey) }}>
                                    {(() => {
                                      const CategoryIcon = MODULE_ACTION_ICON[group.moduleKey] || IconCode;
                                      return <CategoryIcon width={15} height={15} />;
                                    })()}
                                    {group.category}
                                  </span>
                                ) : (
                                  group.category
                                )}
                              </div>
                              <div className="ScriptsActionsPalette">
                                {group.items.map((item) => {
                                  const Icon = item.icon || IconCode;
                                  const disabledByPerm = item.type === 'http_request' && !Boolean(draft.permissions?.allow_http);
                                  const disabled = Boolean(item.comingSoon) || disabledByPerm;
                                  return (
                                    <button
                                      key={item.type}
                                      type="button"
                                      className={`ScriptsActionTile ${disabled ? 'is-disabled' : ''}`}
                                      onClick={() => insertBlockAt(item.type)}
                                      disabled={disabled}
                                      draggable={!disabled}
                                      onDragStart={(e) => {
                                        setDragCatalogType(item.type);
                                        setDragBlockId('');
                                        e.dataTransfer?.setData?.('application/x-scripts-catalog', item.type);
                                      }}
                                      onDragEnd={clearDragState}
                                      style={{ '--tile-color': item.color || '#94a3b8' }}
                                      title={item.comingSoon ? 'Action à venir' : disabledByPerm ? 'Active d’abord la permission HTTP' : item.hint}
                                    >
                                      <span className="ScriptsActionTileIcon">
                                        <Icon width={15} height={15} />
                                      </span>
                                      <span>{item.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              {screenMode === 'play' ? (
                <div className="Card">
                  <div className="Row" style={{ marginBottom: 10 }}>
                    <div className="TileTitle">Exécution — {draft.name || `Script #${draft.id}`}</div>
                    <div className="Badges">
                      <button type="button" className="Button Button--ghost" onClick={() => setScreenMode('list')}>
                        Retour
                      </button>
                      <button type="button" className="Button Button--ghost" onClick={() => setScreenMode('edit')}>
                        Éditer
                      </button>
                      <button
                        type="button"
                        className="Button"
                        onClick={runSelectedScript}
                        disabled={running || !Boolean(draft.is_enabled)}
                      >
                        {running ? 'Exécution…' : 'Exécuter'}
                      </button>
                    </div>
                  </div>

                  <div className="Field">
                    <label className="Label">Entrée JSON</label>
                    <textarea
                      className="Textarea ScriptsCode"
                      rows={5}
                      value={runInputText}
                      onChange={(e) => setRunInputText(e.target.value)}
                      placeholder='{"name":"Martin"}'
                    />
                  </div>

                  {runError ? (
                    <div className="Error" role="alert" style={{ marginTop: 10 }}>
                      {runError}
                    </div>
                  ) : null}

                  {runResult?.run ? (
                    <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
                      <span className={`Badge ${runResult.run.status === 'success' ? '' : 'Badge--danger'}`}>
                        {runResult.run.status === 'success' ? 'Succès' : 'Erreur'}
                      </span>
                      <span className="Badge">{formatDateTime(runResult.run.finished_at || runResult.run.started_at)}</span>
                      <span className="Badge">{Number(runResult.run.duration_ms || 0)} ms</span>
                    </div>
                  ) : null}

                  {runResult?.output ? (
                    <div className="ScriptsRunOutput" style={{ marginTop: 12 }}>
                      <div>
                        <div className="LabelStrong">Logs</div>
                        <div className="ScriptsLogList">
                          {(runResult.output.logs || []).map((line, idx) => (
                            <div key={`log_${idx}`} className="Muted">
                              {line}
                            </div>
                          ))}
                          {(runResult.output.logs || []).length === 0 ? <div className="Muted">Aucun log.</div> : null}
                        </div>
                      </div>

                      <div>
                        <div className="LabelStrong">Variables</div>
                        <pre className="ScriptsJsonPreview">{prettyJson(runResult.output.vars || {})}</pre>
                      </div>

                      <div>
                        <div className="LabelStrong">Fichiers générés</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {(runResult.output.generated_files || []).map((f, idx) => (
                            <div key={`file_${idx}`} className="Badge" style={{ justifyContent: 'space-between' }}>
                              <span className="OneLine" style={{ maxWidth: 420 }}>
                                {String(f.filename || 'fichier')}
                              </span>
                              <button
                                type="button"
                                className="Button Button--ghost Button--icon"
                                onClick={() => downloadGeneratedFile(f)}
                                title="Télécharger"
                              >
                                <IconDownload />
                              </button>
                            </div>
                          ))}
                          {(runResult.output.generated_files || []).length === 0 ? <div className="Muted">Aucun fichier.</div> : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div style={{ marginTop: 14 }}>
                    <div className="Row" style={{ marginBottom: 8 }}>
                      <div className="LabelStrong">Historique récent</div>
                      <button
                        type="button"
                        className="Button Button--ghost Button--danger"
                        onClick={() => setModal({ type: 'clear-runs', saving: false, error: '' })}
                        disabled={scriptRuns.length === 0}
                      >
                        <span className="ButtonInline">
                          <IconTrash /> Vider l’historique
                        </span>
                      </button>
                    </div>
                    <div className="List" role="list">
                      {scriptRuns.slice(0, 10).map((r) => (
                        <div key={`run_${r.id}_${r.started_at}`} className="ListItem">
                          <div style={{ minWidth: 0 }}>
                            <div className="ButtonInline" style={{ gap: 8, fontWeight: 700 }}>
                              {r.status === 'success' ? <IconCheck width={16} height={16} /> : <IconBan width={16} height={16} />}
                              {r.status === 'success' ? 'Succès' : 'Erreur'}
                            </div>
                            <div className="Muted" style={{ marginTop: 6 }}>
                              {formatDateTime(r.finished_at || r.started_at)} • {Number(r.duration_ms || 0)} ms
                            </div>
                            {r.error_text ? <div className="Error" style={{ marginTop: 8 }}>{r.error_text}</div> : null}
                          </div>
                        </div>
                      ))}
                      {scriptRuns.length === 0 ? <div className="Muted">Aucune exécution pour ce script.</div> : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {runPrompt && currentPromptStep ? (
        <Modal
          title={`Entrée utilisateur (${Number(runPrompt.index || 0) + 1}/${runPrompt.steps.length})`}
          icon={<IconEdit />}
          onClose={() => setRunPrompt(null)}
          submitOnEnter
          onSubmit={submitPromptStep}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setRunPrompt(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={previousPromptStep}
                disabled={Number(runPrompt.index || 0) <= 0}
              >
                Précédent
              </button>
              <button type="button" className="Button" onClick={submitPromptStep}>
                {Number(runPrompt.index || 0) >= runPrompt.steps.length - 1 ? 'Exécuter' : 'Suivant'}
              </button>
            </>
          }
        >
          <div className="LabelStrong">{currentPromptStep.label || 'Saisie'}</div>
          {currentPromptStep.type === 'ask_text' ? (
            <>
              <div className="Muted" style={{ marginTop: 6 }}>
                {`Contraintes: ${
                  currentPromptStep.min_length !== '' ? `min ${currentPromptStep.min_length}` : 'min libre'
                } • ${
                  currentPromptStep.max_length !== '' ? `max ${currentPromptStep.max_length}` : 'max libre'
                }`}
              </div>
              <div className="Field" style={{ marginTop: 10 }}>
                <input
                  className="Input"
                  value={String(runPrompt.values?.[currentPromptStep.key] ?? '')}
                  placeholder={String(currentPromptStep.placeholder || '')}
                  onChange={(e) => patchPromptValue(e.target.value)}
                />
              </div>
            </>
          ) : null}
          {currentPromptStep.type === 'ask_number' ? (
            <>
              <div className="Muted" style={{ marginTop: 6 }}>
                {`Contraintes: ${
                  currentPromptStep.min !== '' ? `min ${currentPromptStep.min}` : 'min libre'
                } • ${
                  currentPromptStep.max !== '' ? `max ${currentPromptStep.max}` : 'max libre'
                } • ${
                  currentPromptStep.allow_decimals
                    ? currentPromptStep.max_decimals !== ''
                      ? `décimales max ${currentPromptStep.max_decimals}`
                      : 'décimales autorisées'
                    : 'entier uniquement'
                }`}
              </div>
              <div className="Field" style={{ marginTop: 10 }}>
                <input
                  className="Input"
                  type="number"
                  step={currentPromptStep.allow_decimals ? 'any' : '1'}
                  value={String(runPrompt.values?.[currentPromptStep.key] ?? '')}
                  onChange={(e) => patchPromptValue(e.target.value)}
                />
              </div>
            </>
          ) : null}
          {currentPromptStep.type === 'ask_select' ? (
            <div className="Field" style={{ marginTop: 10 }}>
              <div className="ScriptsPromptChoices">
                {(currentPromptStep.options || []).map((opt) => {
                  const currentValue = runPrompt.values?.[currentPromptStep.key];
                  const selected = currentPromptStep.multiple
                    ? Array.isArray(currentValue) && currentValue.map((x) => String(x)).includes(String(opt))
                    : String(currentValue || '') === String(opt);
                  return (
                    <button
                      key={String(opt)}
                      type="button"
                      className={`ScriptsPromptChoice ${selected ? 'is-active' : ''}`}
                      onClick={() => {
                        if (currentPromptStep.multiple) {
                          const arr = Array.isArray(currentValue) ? currentValue.map((x) => String(x)) : [];
                          const value = String(opt);
                          if (arr.includes(value)) patchPromptValue(arr.filter((x) => x !== value));
                          else patchPromptValue([...arr, value]);
                        } else {
                          patchPromptValue(String(opt));
                        }
                      }}
                    >
                      {String(opt)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {currentPromptStep.type === 'ask_confirm' ? (
            <div className="Field" style={{ marginTop: 10 }}>
              <div className="ButtonInline" style={{ gap: 10 }}>
                <button
                  type="button"
                  className={`Button ${Boolean(runPrompt.values?.[currentPromptStep.key]) ? '' : 'Button--ghost'}`}
                  onClick={() => patchPromptValue(true)}
                >
                  Oui
                </button>
                <button
                  type="button"
                  className={`Button ${!Boolean(runPrompt.values?.[currentPromptStep.key]) ? '' : 'Button--ghost'}`}
                  onClick={() => patchPromptValue(false)}
                >
                  Non
                </button>
              </div>
            </div>
          ) : null}
          {runPrompt.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {runPrompt.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {runDisplay && Array.isArray(runDisplay.items) && runDisplay.items.length > 0 ? (
        <Modal
          title={String(runDisplay.items?.[runDisplay.index]?.title || 'Afficher')}
          icon={<IconFileText />}
          onClose={() => setRunDisplay(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setRunDisplay(null)}>
                Fermer
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={() => setRunDisplay((cur) => (cur ? { ...cur, index: Math.max(0, Number(cur.index || 0) - 1) } : cur))}
                disabled={Number(runDisplay.index || 0) <= 0}
              >
                Précédent
              </button>
              <button
                type="button"
                className="Button"
                onClick={() =>
                  setRunDisplay((cur) =>
                    cur
                      ? {
                          ...cur,
                          index: Math.min((cur.items?.length || 1) - 1, Number(cur.index || 0) + 1),
                        }
                      : cur
                  )
                }
                disabled={Number(runDisplay.index || 0) >= (runDisplay.items.length - 1)}
              >
                Suivant
              </button>
            </>
          }
        >
          <div className="ScriptsDisplayText">{String(runDisplay.items?.[runDisplay.index]?.text || '')}</div>
          <div className="Muted" style={{ marginTop: 10 }}>
            {Number(runDisplay.index || 0) + 1} / {runDisplay.items.length}
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'new-script' ? (
        <Modal
          title="Nouveau script"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={submitCreateScriptModal}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" onClick={submitCreateScriptModal} disabled={modal.saving}>
                {modal.saving ? 'Création…' : 'Créer le script'}
              </button>
            </>
          }
        >
          <div className="Seg" role="group" aria-label="Mode de création">
            <button
              type="button"
              className={`SegButton ${modal.mode === 'create' ? 'is-active' : ''}`}
              onClick={() => setModal((m) => (m?.type === 'new-script' ? { ...m, mode: 'create', error: '' } : m))}
              disabled={modal.saving}
            >
              Créer
            </button>
            <button
              type="button"
              className={`SegButton ${modal.mode === 'import' ? 'is-active' : ''}`}
              onClick={() => setModal((m) => (m?.type === 'new-script' ? { ...m, mode: 'import', error: '' } : m))}
              disabled={modal.saving}
            >
              Importer JS
            </button>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Nom du script</label>
            <input
              className="Input"
              value={String(modal.name || '')}
              onChange={(e) => setModal((m) => (m?.type === 'new-script' ? { ...m, name: e.target.value } : m))}
              disabled={modal.saving}
              placeholder="Ex: Synchronisation menu"
            />
          </div>

          {modal.mode === 'import' ? (
            <>
              <input
                ref={createFileRef}
                type="file"
                accept=".js,.mjs,.cjs,.txt"
                style={{ display: 'none' }}
                onChange={(e) => {
                  pickNewScriptFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <div className="Field" style={{ marginTop: 10 }}>
                <label className="Label">Script JS à importer</label>
                <div className="InputRow">
                  <input
                    className="Input"
                    value={String(modal.imported_filename || '')}
                    placeholder="Aucun fichier"
                    readOnly
                  />
                  <button
                    type="button"
                    className="Button Button--ghost"
                    onClick={() => createFileRef.current?.click?.()}
                    disabled={modal.saving}
                  >
                    Choisir
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'clear-runs' ? (
        <Modal
          title="Vider l’historique"
          icon={<IconTrash />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                onClick={async () => {
                  await clearScriptRuns();
                  setModal(null);
                }}
              >
                Vider
              </button>
            </>
          }
        >
          <div className="Muted">Supprimer l’historique d’exécution de ce raccourci ?</div>
        </Modal>
      ) : null}

      {modal?.type === 'delete-script' ? (
        <Modal
          title="Supprimer le script"
          icon={<IconTrash />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button Button--danger" onClick={deleteSelected} disabled={modal.saving}>
                {modal.saving ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer <strong>{selectedScript?.name || 'ce script'}</strong> ?
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
